import { QuizWithProctoringOptions } from "./assessments-page-helper";
import {
	ConditionalWrapper,
	EditQuizWrapper,
	EditQuizWrapperRef,
	api,
	VerityApi,
	useMobileLayout,
	EditQuizWrapperProps,
	UnsavedAssessmentInfo,
	EditQuizModalSkeleton,
} from "@verity/components";
import { useEffect, useRef, useState, useContext } from "react";
import { coreToast } from "@verity/components/core";
import i18n from "../../../i18n";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { Prisma } from "@verity/database/prisma";
import { PORTAL_TOAST_CONTAINER_ID } from "../../shared";
import { Id } from "react-toastify";
import { PortalControllerContext } from "../../context";
import verityLogger from "../../../helpers/logger";

type EditAssessmentOptionsPageProps = {
	assessment: UnsavedAssessmentInfo;
	afterSave?: (options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0]) => void;
	standaloneAssessmentEditQuizPageRef: React.RefObject<EditQuizWrapperRef>;
	setEnforcedProfilesOnlyEditQuizPage?: (value: boolean) => void;
} & Required<
	Pick<
		EditQuizWrapperProps,
		| "setIsFinalPage"
		| "onBackEnabledChange"
		| "onNextEnabledChange"
		| "onSaveEnabledChange"
		| "onSaveProfileChange"
		| "uploadedExemptedStudentsMap"
		| "setUploadedExemptedStudentsMap"
		| "onClose"
		| "forceStartOnFirstPage"
		| "quizType"
	>
> &
	Pick<EditQuizWrapperProps, "step" | "setStep" | "setIsFirstPage">;

export default function EditAssessmentOptionsPage(props: EditAssessmentOptionsPageProps) {
	const { portalContext } = useContext(PortalControllerContext);
	const { lms, instituteIntegrationHostname, lmsProvidedCourseId } = portalContext;

	const [quizInfo, setQuizInfo] = useState<UnsavedAssessmentInfo | null>(null);
	const quiz = props.assessment;
	const isMobileLayout = useMobileLayout();
	const loadingToastId = useRef<Id>();

	useEffect(() => {
		void (async () => {
			if (!quiz) {
				return;
			}
			props.setEnforcedProfilesOnlyEditQuizPage?.(await api.areProctoringProfilesEnforced());
			if (!lmsProvidedCourseId) {
				throw new Error(`context missing: lmsProvidedCourseId=${lmsProvidedCourseId}`);
			}
			setQuizInfo({
				lms,
				hostname: instituteIntegrationHostname,
				courseId: lmsProvidedCourseId,
				quizId: quiz.quizId,
			});
		})();
	}, []);

	const [uploadedProfilesMap, setUploadedProfilesMap] = useState<Map<number, ProctoringOptionProfileV2>>(new Map());

	function hasQuizInfo(quiz: unknown): quiz is QuizWithProctoringOptions {
		return (quiz as QuizWithProctoringOptions).quizInfo !== undefined;
	}

	const quizName = hasQuizInfo(quiz)
		? `${((quiz.quizInfo as Prisma.JsonObject) ?? {})["name"]?.toString() ?? "?"}`
		: undefined;

	return (
		<ConditionalWrapper
			condition={isMobileLayout}
			whenTrue={(children) => <div style={{ paddingBottom: "50px", marginTop: "10px" }}>{children}</div>}
		>
			{quizInfo ? (
				<EditQuizWrapper
					quizName={quizName}
					quizInfo={quizInfo}
					onStartSaveOptions={() => {
						loadingToastId.current = coreToast.loading(
							i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.applyingChanges"),
							{
								containerId: PORTAL_TOAST_CONTAINER_ID,
							},
						);
					}}
					onSaveOptions={(success, messages, options) => {
						if (success) {
							props.afterSave?.(options);
							props.onClose();
							coreToast.updateOrShow(
								loadingToastId.current,
								i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.applyMessage"),
								"success",
								{ containerId: PORTAL_TOAST_CONTAINER_ID, autoClose: 2500 },
							);
							loadingToastId.current = undefined;
						} else {
							coreToast.updateOrShow(
								loadingToastId.current,
								<>
									{i18n.t("portal.components.assessmentPage.editQuizOptionPopupTSX.applyFailedMessage")}
									{messages ? ": " : "."}
									{messages?.map((message) => (
										<div key={message}>{message}</div>
									))}
								</>,
								"error",
								{ containerId: PORTAL_TOAST_CONTAINER_ID, autoClose: 2500 },
							);
							loadingToastId.current = undefined;
						}
					}}
					ref={props.standaloneAssessmentEditQuizPageRef}
					onBackEnabledChange={props.onBackEnabledChange}
					onNextEnabledChange={props.onNextEnabledChange}
					onSaveEnabledChange={props.onSaveEnabledChange}
					setIsFinalPage={props.setIsFinalPage}
					setIsFirstPage={props.setIsFirstPage}
					onSaveProfileChange={props.onSaveProfileChange}
					onEnabledChange={() => {
						/**empty */
					}}
					uploadedExemptedStudentsMap={props.uploadedExemptedStudentsMap}
					setUploadedExemptedStudentsMap={props.setUploadedExemptedStudentsMap}
					uploadedProfilesMap={uploadedProfilesMap}
					setUploadedProfilesMap={setUploadedProfilesMap}
					forceStartOnFirstPage={props.forceStartOnFirstPage}
					logger={verityLogger}
					step={props.step}
					setStep={props.setStep}
					onClose={props.onClose}
					quizType={props.quizType}
					entryPoint="lti-page"
				/>
			) : (
				<EditQuizModalSkeleton />
			)}
		</ConditionalWrapper>
	);
}
